import type { HandFrame, HandLandmark, HandSample } from '../domain/types'
import { assignSlots, emptyMemory, type SlotMemory } from './handSlots'

// Assigns hands to persistent spatial slots, then smooths each slot's landmarks
// with a 1€ filter: steady hands get heavy smoothing (no jitter), fast hands get
// almost none (no lag), which is what a playable instrument needs.

interface OneEuroOptions {
  minCutoffHz: number
  beta: number
  derivativeCutoffHz: number
}

const DEFAULT_OPTIONS: OneEuroOptions = { minCutoffHz: 2.2, beta: 3, derivativeCutoffHz: 2 }

class OneEuro {
  private previous: number | null = null
  private previousDerivative = 0

  constructor(private readonly options: OneEuroOptions) {}

  filter(value: number, dtSeconds: number): number {
    if (this.previous === null) {
      this.previous = value
      return value
    }
    const derivative = (value - this.previous) / dtSeconds
    const derivativeAlpha = alphaFor(this.options.derivativeCutoffHz, dtSeconds)
    this.previousDerivative = this.previousDerivative + (derivative - this.previousDerivative) * derivativeAlpha
    const cutoff = this.options.minCutoffHz + this.options.beta * Math.abs(this.previousDerivative)
    const alpha = alphaFor(cutoff, dtSeconds)
    this.previous = this.previous + (value - this.previous) * alpha
    return this.previous
  }
}

function alphaFor(cutoffHz: number, dtSeconds: number): number {
  const tau = 1 / (2 * Math.PI * cutoffHz)
  return 1 / (1 + tau / dtSeconds)
}

interface SlotFilterState {
  filters: OneEuro[]
  landmarkCount: number
  timestampMs: number
}

export class TemporalHandFilter {
  private memory: [SlotMemory, SlotMemory] = emptyMemory()
  private readonly states = new Map<number, SlotFilterState>()

  constructor(private readonly options: OneEuroOptions = DEFAULT_OPTIONS) {}

  push(frame: HandFrame): HandFrame {
    const assignment = assignSlots(frame.hands, this.memory, frame.timestampMs)
    this.memory = assignment.memory
    const hands: HandSample[] = []
    assignment.slots.forEach((hand, slot) => {
      if (!hand) {
        this.states.delete(slot)
        return
      }
      hands.push(this.smooth(hand, slot, frame.timestampMs))
    })
    return { ...frame, hands }
  }

  reset(): void {
    this.memory = emptyMemory()
    this.states.clear()
  }

  private smooth(hand: HandSample, slot: number, timestampMs: number): HandSample {
    let state = this.states.get(slot)
    if (!state || state.landmarkCount !== hand.landmarks.length) {
      state = { filters: hand.landmarks.flatMap(() => [new OneEuro(this.options), new OneEuro(this.options), new OneEuro(this.options)]), landmarkCount: hand.landmarks.length, timestampMs }
      this.states.set(slot, state)
      hand.landmarks.forEach((point, index) => {
        state!.filters[index * 3]!.filter(point.x, 1 / 30)
        state!.filters[index * 3 + 1]!.filter(point.y, 1 / 30)
        state!.filters[index * 3 + 2]!.filter(point.z, 1 / 30)
      })
      return hand
    }
    const dt = Math.max(1 / 240, (timestampMs - state.timestampMs) / 1_000)
    state.timestampMs = timestampMs
    const landmarks: HandLandmark[] = hand.landmarks.map((point, index) => ({
      x: state!.filters[index * 3]!.filter(point.x, dt),
      y: state!.filters[index * 3 + 1]!.filter(point.y, dt),
      z: state!.filters[index * 3 + 2]!.filter(point.z, dt)
    }))
    return { ...hand, landmarks }
  }
}
